import React, { useEffect } from 'react';

import { Select } from '@/pages/LandingPage/sections/ContactUs/components/ui/Select';
import { useTheme } from '@/services/theme';
import { LabelContainer } from '@/pages/LandingPage/sections/ContactUs/components/ui/LabelContainer';
import { Label } from '@/pages/LandingPage/sections/ContactUs/components/ui/Label';
import { FormField } from '@/pages/LandingPage/sections/ContactUs/components/ui/FormField';
import { OptionsContainer } from '@/pages/LandingPage/sections/ContactUs/components/ui/OptionsContainer';
import { Option } from '@/pages/LandingPage/sections/ContactUs/components/ui/Option';
import { ErrorLabel } from '@/pages/LandingPage/sections/ContactUs/components/ui/ErrorLabel';

export type ContactFormSelectOption = {
  label: string;
  value: string;
};

type ContactFormSelectProps = {
  label: string;
  placeholder?: string;
  isRequired?: boolean;
  type: 'select';
  options: ContactFormSelectOption[];
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

export const ContactFormSelect: React.FC<ContactFormSelectProps> = ({
  placeholder,
  options,
  isRequired,
  label,
  value,
  onChange,
  error,
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = React.useState(true);
  const ref = React.useRef<HTMLDivElement>(null);

  function onSelectHandler(value: string) {
    return () => {
      onChange(value);
    };
  }

  function toggleOptionsVisibility() {
    setIsOpen(!isOpen);
  }

  useEffect(() => {
    setIsOpen(false);
  }, [value]);

  const selectedValue = options.find(e => e.value === value);
  const selectPlaceholder = selectedValue ? selectedValue.label : placeholder;

  useEffect(() => {
    function onClickSomeWhere(event: MouseEvent) {
      if (ref.current) {
        let target = event.target as HTMLDivElement | ParentNode;
        let found = false;
        while (!found) {
          if (target && target.isSameNode(ref.current)) {
            found = true;
          } else {
            if (target.parentNode) {
              target = target.parentNode;
            } else {
              break;
            }
          }
        }
        if (!found) {
          setIsOpen(false);
        }
      }
    }

    document.body.addEventListener('click', onClickSomeWhere);

    return () => {
      document.body.removeEventListener('click', onClickSomeWhere);
    };
  }, []);

  return (
    <FormField ref={ref}>
      <LabelContainer>
        <Label onClick={toggleOptionsVisibility} theme={theme} isRequired={isRequired}>
          {label}
        </Label>
      </LabelContainer>
      <Select className={error ? 'error' : ''} theme={theme} onClick={toggleOptionsVisibility}>
        {selectPlaceholder}
        {isOpen && (
          <OptionsContainer theme={theme}>
            {options.map(option => (
              <Option
                theme={theme}
                className={option.value === value ? 'active' : ''}
                key={option.value}
                onClick={onSelectHandler(option.value)}
              >
                {option.label}
              </Option>
            ))}
          </OptionsContainer>
        )}
      </Select>
      {error && <ErrorLabel theme={theme}>{error}</ErrorLabel>}
    </FormField>
  );
};
